import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSyncedRefresh } from "@/hooks/useLiveSync";

export const CLAUDE = "#D97757";
const linkFor = (r) => (r?.bid_id ? `/bids/${r.bid_id}` : r?.client_id ? `/clients/${r.client_id}` : r?.lead_id ? `/leads/${r.lead_id}` : "/pipeline");
const ago = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
const nameOf = (r) => r.name || (r.surface === "library" ? "the libraries" : "the pipeline");

// Claude's sunburst mark.
export const ClaudeMark = ({ className = "w-4 h-4", color = CLAUDE }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * Math.PI) / 6, r1 = i % 2 ? 5.2 : 4.2, r2 = i % 2 ? 9.6 : 10.6;
      return <line key={i} x1={12 + r1 * Math.cos(a)} y1={12 + r1 * Math.sin(a)} x2={12 + r2 * Math.cos(a)} y2={12 + r2 * Math.sin(a)} />;
    })}
  </svg>
);

export function usePresence() {
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get("/claude/presence").then((r) => setP(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useSyncedRefresh(load);
  return p;
}

// Top-bar mark: pulses while Claude is working, dims when idle. Click for the details.
export function ClaudePresenceChip() {
  const p = usePresence();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const cur = p?.current;
  if (!cur) return null;
  const rows = (p.touched || []).length ? p.touched : [cur];
  return (
    <div className="relative hidden sm:block" ref={ref}>
      <button data-testid="claude-presence-chip" onClick={() => setOpen((o) => !o)} aria-label={cur.active ? "Claude is working" : "Claude is idle"}
        title={cur.active ? `Claude · ${nameOf(cur)} · ${cur.label}` : `Claude · last on ${nameOf(cur)}, ${ago(cur.at)}`}
        className={`relative grid place-items-center h-9 w-9 rounded-md transition-colors hover:bg-white/10 ${cur.active ? "" : "opacity-40 hover:opacity-80"}`}>
        {cur.active && <span className="absolute inset-1.5 rounded-full animate-ping opacity-40" style={{ background: CLAUDE }} data-testid="claude-presence-pulse" />}
        <ClaudeMark className="relative w-[18px] h-[18px]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 rounded-xl border border-slate-200 bg-white shadow-xl p-1.5 z-50 text-left" data-testid="claude-presence-list">
          <div className="flex items-center gap-2 px-2.5 pt-1.5 pb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: CLAUDE }}>
            <ClaudeMark className="w-3.5 h-3.5" /> {cur.active ? "Claude is working" : `Claude · idle since ${ago(cur.at)}`}
          </div>
          {rows.map((r, i) => (
            <button key={i} data-testid={`claude-presence-row-${i}`} onClick={() => { setOpen(false); navigate(linkFor(r)); }}
              className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-orange-50 text-xs transition-colors">
              <div className="font-semibold text-slate-900 truncate">{nameOf(r)}</div>
              <div className="text-slate-500 truncate">{r.label} · {ago(r.at)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Slim strip on an open record: the last 20 changes via Claude, live, with Undo where it applies.
export function LiveClaudeChanges({ bidId }) {
  const [rows, setRows] = useState([]);
  const [undoRp, setUndoRp] = useState(null);
  const load = useCallback(() => bidId && api.get(`/claude/bids/${bidId}/changes`).then((r) => { setRows((r.data?.changes || []).slice(0, 20)); setUndoRp(r.data?.undo_rp || null); }).catch(() => {}), [bidId]);
  useEffect(() => { load(); }, [load]);
  useSyncedRefresh(load);
  const undo = async () => { if (!undoRp) return; try { await api.post(`/claude/bids/${bidId}/restore`, { rp_id: undoRp.rp_id }); load(); } catch { /* shown by API */ } };
  if (!rows.length) return null;
  return (
    <div className="mb-3 rounded-lg border px-3 py-2" style={{ borderColor: `${CLAUDE}55`, background: `${CLAUDE}0D` }} data-testid="live-claude-changes">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: CLAUDE }}><ClaudeMark className="w-3 h-3" /> Live changes via Claude</div>
      <ul className="space-y-0.5 max-h-28 overflow-auto">
        {rows.map((c, i) => (
          <li key={c.change_id || i} className="flex items-center justify-between gap-3 text-xs" data-testid={`live-change-${i}`}>
            <span className="truncate text-slate-800">{c.summary}</span>
            <span className="shrink-0 text-slate-500">{ago(c.at)}{i === 0 && undoRp && !c.undone_at && <button onClick={undo} className="ml-2 text-[#003E87] hover:underline" data-testid="live-change-undo">Undo</button>}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
